'use client';

import { useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { CheckCircle, XCircle, Clock, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queries/queryKeys';


interface TestAttempt {
  id: string;
  test_id: string;
  score: number | null;
  passed: boolean | null;
  completed_at: string | null;
  attempt_number: number | null;
  test_title: string;
}

interface TestHistoryProps {
  eventId: string;
  enrollmentId: string;
  onViewAttempt?: (attemptId: string, testId: string) => void;
  showDivider?: boolean;
}

const ITEMS_PER_PAGE = 10;

export default function TestHistory({ eventId, enrollmentId, onViewAttempt, showDivider = true }: TestHistoryProps) {
  const { t, language } = useLanguage();
  const [loadingMore, setLoadingMore] = useState(false);
  const [extraAttempts, setExtraAttempts] = useState<TestAttempt[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const supabase = createClient();

  // Fetch first page via TanStack Query (auto-refetches on invalidation)
  const { data: firstPageData, isLoading: loading } = useQuery({
    queryKey: queryKeys.events.testHistory(eventId, enrollmentId),
    queryFn: async () => {
      // Get total count
      const { count } = await supabase
        .from('test_attempts')
        .select('id, tests!inner(content_id)', { count: 'exact', head: true })
        .eq('enrollment_id', enrollmentId)
        .eq('tests.content_id', eventId)
        .not('completed_at', 'is', null);

      // Fetch first page
      const { data, error } = await supabase
        .from('test_attempts')
        .select(`
          id,
          test_id,
          score,
          passed,
          completed_at,
          attempt_number,
          tests!inner (
            title,
            content_id
          )
        `)
        .eq('enrollment_id', enrollmentId)
        .eq('tests.content_id', eventId)
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false })
        .range(0, ITEMS_PER_PAGE - 1);

      if (error) throw error;

      const attempts: TestAttempt[] = (data || []).map(item => ({
        id: item.id,
        test_id: item.test_id,
        score: item.score,
        passed: item.passed,
        completed_at: item.completed_at,
        attempt_number: item.attempt_number,
        test_title: (item.tests as { title: string }).title,
      }));

      return {
        attempts,
        totalCount: count || 0,
        hasMore: attempts.length === ITEMS_PER_PAGE,
      };
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!eventId && !!enrollmentId,
  });

  // Combine first page (from query) with extra pages (from manual load-more)
  const attempts = [...(firstPageData?.attempts ?? []), ...extraAttempts];
  const totalCount = firstPageData?.totalCount ?? 0;

  // Reset extra attempts when first page data changes (e.g., after invalidation)
  const firstPageIds = firstPageData?.attempts?.map(a => a.id).join(',');
  const [prevFirstPageIds, setPrevFirstPageIds] = useState<string | undefined>();
  if (firstPageIds !== prevFirstPageIds) {
    setPrevFirstPageIds(firstPageIds);
    if (extraAttempts.length > 0) {
      setExtraAttempts([]);
      setHasMore(firstPageData?.hasMore ?? true);
    }
  }

  const handleLoadMore = useCallback(async () => {
    const currentCount = attempts.length;
    if (loadingMore) return;

    setLoadingMore(true);
    try {
      const { data, error } = await supabase
        .from('test_attempts')
        .select(`
          id,
          test_id,
          score,
          passed,
          completed_at,
          attempt_number,
          tests!inner (
            title,
            content_id
          )
        `)
        .eq('enrollment_id', enrollmentId)
        .eq('tests.content_id', eventId)
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false })
        .range(currentCount, currentCount + ITEMS_PER_PAGE - 1);

      if (error) {
        console.error('Error fetching more test attempts:', error);
        return;
      }

      const newAttempts: TestAttempt[] = (data || []).map(item => ({
        id: item.id,
        test_id: item.test_id,
        score: item.score,
        passed: item.passed,
        completed_at: item.completed_at,
        attempt_number: item.attempt_number,
        test_title: (item.tests as { title: string }).title,
      }));

      setExtraAttempts(prev => [...prev, ...newAttempts]);
      setHasMore(newAttempts.length === ITEMS_PER_PAGE);
    } finally {
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempts.length, enrollmentId, eventId, loadingMore]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);

    if (language === 'uk') {
      const monthNames = ['січня', 'лютого', 'березня', 'квітня', 'травня', 'червня', 'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня'];
      const day = date.getDate();
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day} ${month} ${year}, ${hours}:${minutes}`;
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className={showDivider ? 'mt-8 pt-8 border-t border-gray-200' : ''}>
        <div className="flex items-center gap-2 mb-4">
          <History className="h-5 w-5 text-gray-600" />
          <h3 className="text-xl font-bold text-gray-900">{t('event.testHistory.title')}</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (attempts.length === 0) {
    return null; // Don't show section if no history
  }

  const remainingCount = totalCount - attempts.length;

  return (
    <div className={showDivider ? 'mt-8 pt-8 border-t border-gray-200' : ''}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <History className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-bold text-gray-900">{t('event.testHistory.title')}</h3>
        <span className="text-sm text-gray-500">({totalCount})</span>
      </div>

      {/* Attempts List */}
      <div className="space-y-3">
        {attempts.map((attempt) => (
          <div
            key={attempt.id}
            className={`flex items-center justify-between p-4 rounded-lg border-l-4 ${
              attempt.passed
                ? 'bg-green-50 border-green-600'
                : 'bg-red-50 border-red-600'
            }`}
          >
            <div className="flex items-start gap-3 flex-1 min-w-0">
              {/* Status Icon */}
              <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${
                attempt.passed ? 'bg-green-600' : 'bg-red-600'
              }`}>
                {attempt.passed ? (
                  <CheckCircle className="h-5 w-5 text-white" />
                ) : (
                  <XCircle className="h-5 w-5 text-white" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 break-words">
                  {attempt.test_title}
                </p>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm">
                  {/* Score + Status */}
                  <span className={`font-semibold ${
                    attempt.passed ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {attempt.score ?? 0}% • {attempt.passed ? t('event.testHistory.passed') : t('event.testHistory.failed')}
                  </span>

                  {/* Date */}
                  {attempt.completed_at && (
                    <span className="text-gray-600 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(attempt.completed_at)}
                    </span>
                  )}
                </div>

                {/* Attempt number */}
                {attempt.attempt_number && (
                  <p className="text-xs text-gray-500 mt-1">
                    {t('event.testHistory.attempt')} {attempt.attempt_number}
                  </p>
                )}
              </div>
            </div>

            {/* View Results button */}
            {onViewAttempt && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewAttempt(attempt.id, attempt.test_id)}
                className="flex-shrink-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
              >
                {t('event.test.viewResults')}
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="mt-4">
          <Button
            onClick={handleLoadMore}
            disabled={loadingMore}
            variant="outline"
            className="w-full"
          >
            {loadingMore
              ? t('common.loading')
              : remainingCount > 0
                ? `${t('event.testHistory.loadMore')} (${remainingCount} ${t('event.testHistory.remaining')})`
                : t('event.testHistory.loadMore')
            }
          </Button>
        </div>
      )}
    </div>
  );
}
